import { useMutation } from "@tanstack/react-query";
import supabase from "@/supabase";

export interface ContractSubmissionData {
  applicationId: string;
  signatureUrl?: string;
  contractPdfUrl?: string;
}

export interface ContractSubmissionResponse {
  success: boolean;
  message?: string;
  data?: {
    signatureUrl?: string;
    contractPdfUrl?: string;
    nextStage?: string;
  };
}

/**
 * Hook for interacting with contract-related cloud functions
 */
export const useContractCloudFunctions = () => {
  /**
   * Submit the driver contract with signature
   */
  const submitDriverContract = useMutation<
    ContractSubmissionResponse,
    Error,
    ContractSubmissionData
  >({
    mutationFn: async (data: ContractSubmissionData) => {
      const { data: response, error } =
        await supabase.functions.invoke<ContractSubmissionResponse>(
          "contract-submit-driver-contract",
          {
            body: data,
          }
        );

      if (error) {
        try {
          // Try to parse the error response body to get the actual error message
          const parsedError = await error.context.json();
          throw new Error(parsedError.message || `Error submitting contract: ${error.message}`);
        } catch (parseError) {
          // If parsing fails, fall back to error.message or default message
          throw new Error(`Error submitting contract: ${error.message}`);
        }
      }

      if (!response) {
        throw new Error(
          "No response received from contract-submit-driver-contract function"
        );
      }

      if (!response.success) {
        throw new Error(
          response.message || "Unknown error submitting contract"
        );
      }

      return response;
    },
  });

  return {
    submitDriverContract,
  };
};
